import { useRef, useState } from 'react';
import type { Lesson, Section } from '@pen/shared';
import { Rich } from '../ui/hooks';
import type { ActProgress } from './embed';
import { chapterAtTime } from './film';

type Video = Extract<Section, { kind: 'video' }>;
const languages = { en: 'English', it: 'Italiano' };

export function VideoSection({ section, language, onAct }: { section: Video; language: Lesson['language']; onAct(progress: ActProgress): void }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [chapter, setChapter] = useState(() => chapterAtTime(section.chapters, 0));
  const previous = useRef('');
  const [error, setError] = useState(false);
  const videoId = `${section.id}-video`;
  function update(video: HTMLVideoElement) {
    const act = chapterAtTime(section.chapters, video.currentTime);
    setChapter(act);
    const signature = `${act}:${video.ended}`;
    if (signature === previous.current) return;
    previous.current = signature;
    onAct({ act, ended: video.ended });
  }
  return <figure className="lesson-figure lesson-video">
    <h2 id={`${videoId}-title`}>{section.title}</h2>
    <video id={videoId} ref={ref} controls playsInline preload="metadata" poster={section.poster ?? undefined} src={section.src} aria-labelledby={`${videoId}-title`} aria-describedby={section.caption ? `${videoId}-caption` : undefined}
      onLoadedMetadata={event => update(event.currentTarget)} onTimeUpdate={event => update(event.currentTarget)} onSeeking={event => update(event.currentTarget)} onSeeked={event => update(event.currentTarget)} onEnded={event => update(event.currentTarget)} onError={() => setError(true)}>
      {section.captions.map(track => <track key={track.lang} kind="captions" src={track.src} srcLang={track.lang} label={languages[track.lang]} default={track.lang === language} />)}
      <a href={section.src}>Download {section.title}</a>
    </video>
    {error && <p className="error" role="alert">This video could not be played. <a href={section.src}>Open the video file</a>.</p>}
    {section.chapters.length > 0 && <nav className="video-chapters" aria-label={`${section.title}: ${language === 'it' ? 'capitoli' : 'chapters'}`}><ol>
      {section.chapters.map((item, index) => <li key={index}><button type="button" className="secondary" aria-controls={videoId} aria-current={chapter === index ? 'true' : undefined} onClick={() => {
        const video = ref.current;
        if (!video) return;
        video.currentTime = item.start;
        update(video);
      }}><span className="video-chapter-number" aria-hidden="true">{index + 1}.</span>{item.title}</button></li>)}
    </ol></nav>}
    {section.caption && <figcaption id={`${videoId}-caption`} className="section-caption"><Rich html={section.caption} /></figcaption>}
  </figure>;
}
